// Admin → Scene scrub, on src/ui: the ONE phrase list (A-3: D-A reaction phrases + S-7 stage directions) and the
// cut-list settings (quiet period, pads, reaction cap, path prefix, rough-cut join offset / voice cross / reaction
// join / completeness; media fallback channels + voice channel; Rough Cut v2: placement mode, take-map model,
// pinned sequence format, whole-take cap, ALL TAKES toggle; Rough Cut v3: join style, straight (default) or jl).
// These are engine settings, not a progress report (DESIGN rev 2.1 §2.6 retires reports, not settings).
// Same calls as before; the data-testid hooks the browser verify scripts use are kept on the same controls.
import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Table from '../ui/Table';
import Skeleton from '../ui/Skeleton';
import ErrorState from '../ui/ErrorState';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { clean } from '../shell/copy';

const KINDS = [['both', 'Reaction and direction'], ['expression', 'Reaction phrase (D-A)'], ['direction', 'Stage direction (S-7)']];
// Q-2 (D-179): the budget keys (take-map / vision caps, screening reserve, the run hard cap) left this list; Admin → Scrub queue only
const SETTINGS = [
  ['quiet_minutes', 'Auto-build quiet period (minutes)', 'number'],
  ['pad_head_sec', 'Head pad before first word (s)', 'number'],
  ['pad_tail_sec', 'Tail pad after last word (s)', 'number'],
  ['reaction_cap_sec', 'Reaction take cap from action (s)', 'number'],
  ['timebase', 'XML timebase (frames)', 'number'],
  // Rough cut (Context.HeliconflowRoughCut.md R-4 / R-5 / R-7)
  ['join_offset_frames', 'Rough cut: picture-to-sound offset at a join (frames)', 'number'],
  ['voice_cross_frames', 'Rough cut: the two voices cross by (frames)', 'number'],
  ['reaction_join_frames', 'Rough cut: offset into and out of a reaction shot (frames)', 'number'],
  ['complete_coverage', 'Rough cut: a take is complete at this share of the scripted words (0 to 1)', 'number'],
  // Media properties (Context.HeliconflowMediaProps.md Phase 4)
  ['audio_channels', 'Audio channels assumed for a file whose probe failed (files are declared with their real count when known)', 'number'],
  ['voice_source_channel', 'Source audio channel carrying the voice (FX3 shoots: 1)', 'number'],
  ['path_prefix_default', 'Clip path prefix template (blank = bare file names; use {file})', 'text'],
  // Rough Cut v2 (Context.HeliconflowRoughCutV2r2.md W-1 / W-5 / W-6 / W-7 / W-9)
  ['placement_mode', 'Placement: how takes are found (takemap = the model reads every clip; state = the on-set cue state machine; legacy = the older dialogue-first matcher)', 'select', ['takemap', 'state', 'legacy']],
  ['takemap_model', 'Take map: the model that reads the clips (a model id, e.g. claude-opus-5; changing it needs no code)', 'text'],
  ['sequence_width', 'Sequence format: width (px). Every sequence in the download is pinned to this', 'number'],
  ['sequence_height', 'Sequence format: height (px). Defaults to 1080 by 1920 (vertical)', 'number'],
  ['whole_take_cap_sec', 'Rough cut: a whole-take fallback longer than this becomes a labelled hole (seconds)', 'number'],
  // Rough Cut v3 (Context.HeliconflowRoughCutV3.md X-1)
  ['join_style', 'Rough cut joins: straight = every join is a straight cut (default); jl = J/L sound offsets chosen by listening to the handles', 'select', ['straight', 'jl']],
  ['include_all_takes', 'Download: also include the 02 ALL TAKES sequence (off = the rough cut only; alternates live in the panel)', 'boolean'],
  // Visual index, the eye (Context.HeliconflowVisualIndex.md V-6)
  ['visual_index_enabled', 'Visual index (the eye): sample frames from every clip and read them with a vision model (off = zero vision calls)', 'boolean'],
  ['visual_model', 'Visual index: the vision model that reads the frames (a model id, e.g. claude-haiku-4-5)', 'text'],
  ['visual_sample_sec', 'Visual index: one frame every N seconds (0.5 to 60)', 'number'],
  // Rough Cut v3b (Context.HeliconflowRoughCutV3b.md Y-1, D-138)
  ['span_check_mode', "The eye's span check: corroborate = a take nobody is mouthing becomes a director feed only when its character is not the clip's bound on-camera character (default); strict = any such take (v3); off = never", 'select', ['corroborate', 'strict', 'off']],
  // Rough Cut v4 (Context.HeliconflowRoughCutV4.md Z-1), the judge
  ['judge_mode', 'The judge: strict = the words check is the gate (default); judge = one model pass reads every real delivery in the story and the room and may place a VARIANT (a changed line the set accepted), with the verbatim words and reason shown', 'select', ['strict', 'judge']],
];

// D-183 F-2: the three engine knobs scoped per script format (no new knobs; the witness density is pinned by D-178, not a setting)
const FORMATS = ['Skit', 'Mockumentary', 'TH', 'Longform'];
const FORMAT_KNOBS = [['pad_tail_sec', 'Tail pad (s)', null], ['join_style', 'Joins', ['straight', 'jl']], ['vo_pass_face', 'VO pass face', ['listener', 'speaker']]];
const BOOL_OPTIONS = [{ value: 'false', label: 'Off' }, { value: 'true', label: 'On' }];

export default function SceneScrubAdmin() {
  const [phrases, setPhrases] = useState([]);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({ phrase: '', kind: 'both' });
  const [edits, setEdits] = useState({});
  const [loadErr, setLoadErr] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get('/api/scene-scrub/phrases'), api.get('/api/scene-scrub/settings')]);
      setPhrases(p.data); setSettings(s.data); setEdits({}); setLoadErr('');
    } catch (e) { setLoadErr(clean(e.response?.data?.error) || 'The scene scrub settings did not load.'); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function addPhrase(e) {
    e.preventDefault();
    if (!draft.phrase.trim()) return;
    try { await api.post('/api/scene-scrub/phrases', draft); setDraft({ phrase: '', kind: 'both' }); toast('Phrase added.'); load(); }
    catch (e2) { toast(clean(e2.response?.data?.error) || 'The phrase did not save. Try again.', { tone: 'danger' }); }
  }
  async function removePhrase(p) {
    const ok = await confirm({ title: `Remove "${clean(p.phrase)}"?`, body: 'Script rows stop matching this phrase on the next scrub.', confirmLabel: 'Remove phrase', destructive: true });
    if (!ok) return;
    try { await api.delete(`/api/scene-scrub/phrases/${p.id}`); toast(`Removed "${clean(p.phrase)}".`); load(); }
    catch (e2) { toast(clean(e2.response?.data?.error) || 'The phrase did not remove. Try again.', { tone: 'danger' }); }
  }
  async function saveSettings() {
    const body = {};
    for (const [k, , type] of SETTINGS) if (k in edits) body[k] = type === 'number' ? Number(edits[k]) : type === 'boolean' ? edits[k] === true || edits[k] === 'true' : edits[k];
    // D-183 F-2: per-format rows; '' = back to the global value (the row is removed)
    for (const [k, v] of Object.entries(edits)) if (k.startsWith('format.')) body[k] = v === '' ? null : (k.endsWith('.pad_tail_sec') ? Number(v) : v);
    if (!Object.keys(body).length) return;
    setSaving(true);
    try { const r = await api.put('/api/scene-scrub/settings', body); setSettings(r.data); setEdits({}); toast('Settings saved.'); }
    catch (e2) { toast(clean(e2.response?.data?.error) || 'The settings did not save. Try again.', { tone: 'danger' }); }
    finally { setSaving(false); }
  }

  if (!loaded) {
    return <div className="ui-region ui-region--pad ui-stack" role="status" aria-label="Loading scene scrub settings"><Skeleton width="30%" /><Skeleton width="80%" /><Skeleton variant="block" height={240} /></div>;
  }
  if (loadErr && !settings) return <ErrorState title="Scene scrub settings did not load" message={loadErr} onRetry={load} />;

  const phraseColumns = [
    { key: 'phrase', label: 'Phrase', width: '36%', render: p => clean(p.phrase) },
    { key: 'kind', label: 'Applies to', render: p => KINDS.find(k => k[0] === p.kind)?.[1] || p.kind },
    { key: 'createdBy', label: 'Added by', render: p => <span className="ui-text-3">{p.createdBy ? clean(p.createdBy) : 'Seed list'}</span> },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 64, render: p => (
      <Button variant="quiet" iconOnly icon={Trash2} label={`Remove ${clean(p.phrase)}`} onClick={() => removePhrase(p)} />
    ) },
  ];

  const formatColumns = [
    { key: 'format', label: 'Format', width: 140 },
    ...FORMAT_KNOBS.map(([knob, label, options]) => ({
      key: knob, label, render: row => {
        const key = `format.${row.format}.${knob}`;
        const cur = key in edits ? edits[key] : (settings[key] ?? '');
        const global = settings[knob];
        const onChange = e => setEdits(d => ({ ...d, [key]: e.target.value }));
        return options
          ? <Select aria-label={`${label} for ${row.format}`} data-testid={`scene-scrub-format-${row.format}-${knob}`} value={String(cur)} onChange={onChange}
              options={[{ value: '', label: `global (${String(global)})` }, ...options]} />
          : <Input aria-label={`${label} for ${row.format}`} data-testid={`scene-scrub-format-${row.format}-${knob}`} type="number" step="any" min="0" max="5"
              placeholder={`global (${global})`} value={cur} onChange={onChange} />;
      },
    })),
  ];

  const dirty = Object.keys(edits).length > 0;

  return (
    <div className="ui-stack ui-stack--loose">
      <section className="ui-section" aria-labelledby="ss-phrases">
        <h2 className="ui-section__title" id="ss-phrases">Phrase list</h2>
        <p className="ui-section__note">
          One list serves two rules. A script row is a reaction scene when its Line cell is empty, or is 6 words or fewer and
          contains a reaction phrase. A row is a stage direction (non-speaking) when its Line is 4 words or fewer, has no
          sentence punctuation and matches a direction phrase. A row with real dialogue is never non-speaking, whatever this
          list says. VO rows are always speaking.
        </p>
        <form className="ui-toolbar" onSubmit={addPhrase}>
          <Input aria-label="New phrase" className="ui-grow" data-testid="scene-scrub-phrase-input" placeholder="New phrase, e.g. shakes head"
            value={draft.phrase} onChange={e => setDraft(d => ({ ...d, phrase: e.target.value }))} />
          <Select aria-label="Applies to" value={draft.kind} onChange={e => setDraft(d => ({ ...d, kind: e.target.value }))}
            options={KINDS.map(([value, label]) => ({ value, label }))} />
          <Button type="submit" variant="secondary" icon={Plus}>Add</Button>
        </form>
        <div className="ui-region" data-testid="scene-scrub-phrases">
          <Table caption="Phrase list" columns={phraseColumns} rows={phrases}
            empty={{ message: 'No phrases yet. Add the reactions and stage directions your scripts use.' }} />
        </div>
      </section>

      {settings && (
        <section className="ui-section" aria-labelledby="ss-settings">
          <h2 className="ui-section__title" id="ss-settings">Cut list settings</h2>
          <div className="ui-region ui-region--pad ui-list">
            {SETTINGS.map(([k, label, type, options]) => (
              <div key={k} className="ui-pair">
                <span>{label}</span>
                {type === 'select' ? (
                  <Select aria-label={label} data-testid={`scene-scrub-setting-${k}`} value={k in edits ? edits[k] : (settings[k] ?? options[0])}
                    onChange={e => setEdits(d => ({ ...d, [k]: e.target.value }))} options={options} />
                ) : type === 'boolean' ? (
                  <Select aria-label={label} data-testid={`scene-scrub-setting-${k}`} value={String(k in edits ? edits[k] : (settings[k] === true || String(settings[k]) === 'true'))}
                    onChange={e => setEdits(d => ({ ...d, [k]: e.target.value === 'true' }))} options={BOOL_OPTIONS} />
                ) : (
                  <Input aria-label={label} data-testid={`scene-scrub-setting-${k}`} type={type} step={type === 'number' ? 'any' : undefined}
                    value={k in edits ? edits[k] : (settings[k] ?? '')} onChange={e => setEdits(d => ({ ...d, [k]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>

          {/* D-183 F-2 (CSW Order 4): the knobs tuned per kind of script, overridable per script format; empty = the global value above */}
          <div className="ui-stack" data-testid="scene-scrub-format-defaults">
            <h3 className="ui-heading">Per-format defaults</h3>
            <p className="ui-section__note">
              A value here wins over the global setting for cards of that script format. Leave it on global to use the setting
              above. Each stored scrub records which value applied.
            </p>
            <div className="ui-region">
              <Table caption="Per-format defaults" columns={formatColumns} rows={FORMATS.map(format => ({ format }))} rowKey={r => r.format} />
            </div>
          </div>

          <div className="ui-toolbar">
            <Button variant="primary" data-testid="scene-scrub-settings-save" loading={saving} disabled={!dirty} onClick={saveSettings}>Save settings</Button>
            <span className="ui-dense ui-text-3">
              Watcher {String(settings.watcher_enabled) === 'false' ? 'disabled' : 'enabled'}. Per-client path prefixes are edited through the API (path_prefix_by_client).
            </span>
          </div>
        </section>
      )}
    </div>
  );
}
